import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useQuizlets } from '@/queries/quizlets';
import { QuizletCard } from './QuizletCard';
import { CircularLoader } from './CircularLoader';
import { debounce } from '@/lib/utils';
import BookCheckIcon from '@/assets/common/book-check.svg?react';
import styles from './QuizletList.module.scss';

export function QuizletList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDailyOnly, setShowDailyOnly] = useState(false);

  const { data: quizlets, isLoading, error } = useQuizlets({
    search: searchTerm,
    is_daily_knowledge: showDailyOnly ? true : undefined,
  });

  const handleSearch = debounce((value: string) => {
    setSearchTerm(value);
  }, 300);

  if (isLoading) {
    return (
      <div className={styles.quizletList__loading}>
        <CircularLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.quizletList}>
        <div className={styles.quizletList__error}>
          Error loading quizlets: {error.message}
        </div>
      </div>
    );
  }

  const isEmpty = !quizlets || quizlets.length === 0;

  return (
    <div className={styles.quizletList}>
      <div className={styles.quizletList__header}>
        <div className={styles.quizletList__headerContent}>
          <div>
            <h1 className={styles.quizletList__title}>Available Knowledge Decks</h1>
            <p className={styles.quizletList__subtitle}>
              Manage and review your knowledge decks
            </p>
          </div>
          <Link to="/quizlets/create">
            <motion.button
              className={styles.quizletList__newButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              + New Quiz
            </motion.button>
          </Link>
        </div>
      </div>

      {!isEmpty && (
        <div className={styles.quizletList__controls}>
          <input
            type="search"
            className={styles.quizletList__search}
            placeholder="Search Knowledge Decks..."
            onChange={(e) => handleSearch(e.target.value)}
          />
          <div className={styles.quizletList__filters}>
            <button
              className={`${styles.quizletList__filterButton} ${showDailyOnly ? styles['quizletList__filterButton--active'] : ''
                }`}
              onClick={() => setShowDailyOnly(!showDailyOnly)}
            >
              ⭐ Daily Knowledge Only
            </button>
          </div>
        </div>
      )}

      {isEmpty ? (
        <motion.div
          className={styles.quizletList__empty}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className={styles.quizletList__emptyIcon}>
            <BookCheckIcon width={64} height={64} />
          </div>
          <h2 className={styles.quizletList__emptyTitle}>No quizlets yet</h2>
          <p className={styles.quizletList__emptyText}>
            Start by creating your first quiz from a prompt!
          </p>
          <Link to="/">
            <motion.button
              className={styles.quizletList__emptyButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Create Quiz
            </motion.button>
          </Link>
        </motion.div>
      ) : (
        <div className={styles.quizletList__grid}>
          {quizlets.map((quizlet, index) => (
            <QuizletCard key={quizlet.id} quizlet={quizlet} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}
